import { useState } from 'react';
import { FileText, ImageOff, Info, MapPin } from 'lucide-react';
import {
  AqilaImageCarousel,
  AqilaNote,
  AqilaGalleryImage,
} from '@agro1desenvolvimento/agro1-design-system/aqila';
import { orDash } from '../../../../utils/schema-parse';
import type { ParsedKml } from '../../../../utils/kml';
import type {
  RelatorioVisitaCampo,
  RelatorioVisitaPonto,
} from '../../schemas/payload.schema';
import GlebaMapa from './gleba-mapa';
import MapaTalhaoModal from './mapa-talhao-modal';
import PontoLightbox from './ponto-lightbox';

const CampoBlock = ({ campo }: { campo: RelatorioVisitaCampo }) => (
  <div className="rv-ponto-card-campo">
    <span className="rv-ponto-card-label">{orDash(campo.label)}</span>
    {campo.type === 'tag' && Array.isArray(campo.value) ? (
      <div className="rv-ponto-card-tags">
        {campo.value.map((item) => (
          <span key={item} className="rv-ponto-card-chip">
            {item}
          </span>
        ))}
      </div>
    ) : (
      <span
        className="rv-ponto-card-value"
        style={{ color: campo.cor ?? 'rgb(var(--color-aqila-ink))' }}
      >
        {orDash(campo.value)}
      </span>
    )}
  </div>
);

interface PontoCardProps {
  ponto: RelatorioVisitaPonto;
  glebaParsed: ParsedKml;
  tituloMapa?: string;
}

const MEDIA_HEIGHT = 280;

const PontoCard = ({ ponto, glebaParsed, tituloMapa }: PontoCardProps) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [mapHover, setMapHover] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);

  const imagens: AqilaGalleryImage[] = (ponto.imagens ?? [])
    .filter((imagem) => !!imagem.url)
    .map((imagem) => ({ src: imagem.url as string }));

  const observacao = orDash(ponto.observacao).trim();
  const temCoordenada = ponto.latitude != null && ponto.longitude != null;
  const hasMapPreview = temCoordenada || glebaParsed.polygons.length > 0;

  const mapPreview =
    hasMapPreview && mapHover ? (
      <div className="rv-ponto-card-map-preview">
        <GlebaMapa
          altura={MEDIA_HEIGHT - 20}
          parsed={glebaParsed}
          pontos={[ponto]}
        />
      </div>
    ) : null;

  const infoButton = hasMapPreview ? (
    <button
      type="button"
      aria-label="Ver no mapa"
      className="rv-ponto-card-map-btn"
      onClick={(event) => {
        event.stopPropagation();
        setMapModalOpen(true);
      }}
      onMouseEnter={() => setMapHover(true)}
      onMouseLeave={() => setMapHover(false)}
      onFocus={() => setMapHover(true)}
      onBlur={() => setMapHover(false)}
      style={{
        background: mapHover
          ? 'rgb(var(--color-aqila-primary))'
          : 'rgba(255,255,255,0.9)',
        color: mapHover ? 'white' : 'rgb(var(--color-aqila-body))',
      }}
    >
      <Info size={16} />
    </button>
  ) : null;

  return (
    <div className="rv-ponto-card">
      <div className="rv-ponto-card-body">
        <div className="rv-ponto-card-media">
          {infoButton}
          {mapPreview}

          {imagens.length > 0 ? (
            <div
              role="button"
              tabIndex={0}
              className="rv-ponto-card-carousel"
              onClick={(event) => {
                if ((event.target as HTMLElement).closest('button')) return;
                setLightboxOpen(true);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setLightboxOpen(true);
                }
              }}
            >
              <AqilaImageCarousel
                images={imagens}
                index={lightboxIndex}
                onIndexChange={setLightboxIndex}
              />
            </div>
          ) : (
            <div className="rv-ponto-card-empty">
              <ImageOff size={40} strokeWidth={1.5} />
              <span className="rv-ponto-card-empty-text">
                Registro sem Mídia
              </span>
            </div>
          )}
        </div>

        <div className="rv-ponto-card-info">
          <span className="rv-ponto-card-tipo">{orDash(ponto.tipo)}</span>

          {temCoordenada ? (
            <div className="rv-ponto-card-coord">
              <MapPin className="rv-ponto-card-coord-icon" size={15} />
              {ponto.latitude}, {ponto.longitude}
            </div>
          ) : null}

          <div className="rv-ponto-card-divider" />

          {(ponto.campos ?? []).length > 0 ? (
            <div className="rv-ponto-card-campos">
              {(ponto.campos ?? []).map((campo) => (
                <CampoBlock key={campo.label} campo={campo} />
              ))}
            </div>
          ) : null}

          {observacao !== '-' && observacao !== '—' && observacao !== '' ? (
            <AqilaNote
              icon={<FileText />}
              label="Observação"
              variant="observation"
            >
              {observacao}
            </AqilaNote>
          ) : null}
        </div>
      </div>

      <PontoLightbox
        images={imagens}
        index={lightboxIndex}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
      />

      {hasMapPreview ? (
        <MapaTalhaoModal
          semHeader
          open={mapModalOpen}
          parsed={glebaParsed}
          pontos={[ponto]}
          titulo={tituloMapa ?? orDash(ponto.tipo)}
          onClose={() => setMapModalOpen(false)}
        />
      ) : null}
    </div>
  );
};

export default PontoCard;
